import { formatMoney } from '../../../util/currency';
import { displayBrand } from '../../../util/listingFieldLabels';
import { LISTING_STATE_PUBLISHED } from '../../../util/types';

import { canonicalListingUrl, canonicalProfileUrl } from './shareTargets';

/**
 * What ShareButton needs to share a thing, built per kind of thing.
 *
 * The button and the dialog are deliberately ignorant of listings and
 * profiles: they take a `share` (where the link goes and how it is tracked)
 * and a `preview` (what the dialog shows the sender). Everything that knows
 * what a listing or a profile *is* lives here, so the second thing to get a
 * share button did not get a second share button.
 *
 * Shapes:
 *   share:   { url, title, text, contentType, itemId }
 *   preview: { image, imageAlt, variants, title, subtitle, detail }
 */

// Thumbnails use the uncropped variants and a CSS centre-crop, like the
// gallery thumbnails — the cropped card variants come out off-centre.
const LISTING_PREVIEW_VARIANTS = ['scaled-small', 'scaled-medium'];
const PROFILE_PREVIEW_VARIANTS = ['square-small', 'square-small2x'];

/**
 * Share props for a listing, or null when it is not published — there is
 * nothing to send anyone to.
 */
export const listingShareProps = ({ listing, listingFields, marketplaceRootURL, intl }) => {
  const isPublished = listing?.attributes?.state === LISTING_STATE_PUBLISHED;
  const listingId = listing?.id?.uuid;
  if (!isPublished || !listingId) {
    return null;
  }
  const { title, price, publicData } = listing.attributes;
  return {
    share: {
      url: canonicalListingUrl(marketplaceRootURL, listingId, title),
      title,
      text: intl.formatMessage({ id: 'ListingPage.shareText' }),
      contentType: 'listing',
      itemId: listingId,
    },
    preview: {
      image: listing.images?.[0] || null,
      imageAlt: title,
      variants: LISTING_PREVIEW_VARIANTS,
      title,
      subtitle: displayBrand(listingFields, publicData),
      detail: price ? formatMoney(intl, price) : null,
    },
    label: intl.formatMessage({ id: 'ListingPage.shareButton' }),
    modalTitle: intl.formatMessage({ id: 'ListingPage.shareModalTitle' }),
  };
};

/**
 * Share props for a public profile.
 *
 * The same button for the owner and for a visitor sending a seller to a
 * friend. Only the share text differs. The preview says how much is for sale,
 * unless nothing is — a "0 plagg" would read as a warning, so it is left out.
 */
export const profileShareProps = ({
  user,
  displayName,
  listingCount = 0,
  isOwn = false,
  marketplaceRootURL,
  intl,
}) => {
  const userId = user?.id?.uuid;
  if (!userId || !displayName) {
    return null;
  }
  const title = intl.formatMessage({ id: 'ProfilePage.socialSharingTitle' }, { name: displayName });
  return {
    share: {
      url: canonicalProfileUrl(marketplaceRootURL, userId),
      title,
      text: intl.formatMessage(
        { id: isOwn ? 'ProfilePage.shareTextOwn' : 'ProfilePage.shareText' },
        { name: displayName }
      ),
      contentType: 'profile',
      itemId: userId,
    },
    preview: {
      // The Avatar component, not a raw image: it draws initials when there
      // is no photo, and it is what the profile page itself shows.
      avatarUser: user,
      image: null,
      imageAlt: displayName,
      variants: PROFILE_PREVIEW_VARIANTS,
      title: displayName,
      subtitle:
        listingCount > 0
          ? intl.formatMessage({ id: 'ProfilePage.shareListingCount' }, { count: listingCount })
          : null,
      detail: null,
    },
    // One label for everyone. Guro: "Del profilen" reads right whether it is
    // your own profile, a friend's, or a business account.
    label: intl.formatMessage({ id: 'ProfilePage.shareButton' }),
    modalTitle: intl.formatMessage({ id: 'ProfilePage.shareModalTitle' }),
    variant: 'labelled',
  };
};
